/**
 * GeoSessionProvider.tsx
 *
 * Singleton React Context that mints a geo-session token ONCE for the entire
 * app and keeps it fresh via proactive refresh, mirroring the pattern used by
 * ProjectionSessionProvider for SSE telemetry stream authentication.
 *
 * Mount once inside AppLayout (router.tsx) so every authenticated page has
 * a valid X-Geo-Token before any /api/v1/* (geocoding / routing) call fires.
 *
 * Token lifecycle:
 *   1. On mount: POST /drt/api/bff/geo-session → { token, expiresAt }
 *   2. Token written to geoTokenStore (read by apiClient request interceptor)
 *   3. Timer scheduled to refresh 60 s before expiresAt
 *   4. On a 401 from any /api/v1/* call, the apiClient interceptor fires
 *      GEO_TOKEN_EXPIRED_EVENT and this provider re-mints (throttled)
 *   5. On unmount / logout: token cleared from geoTokenStore
 *
 * Deduplication (React 18 StrictMode):
 *   A module-level `inflightFetch` promise is shared across double-invoked
 *   effects — only ONE HTTP request is ever made per render cycle.
 *
 * Tenant ID resolution:
 *   The maps proxy validates X-Tenant-ID against the tenantId embedded in the
 *   GeoSessionRecord at mint time.  The BFF derives that tenantId from the
 *   server-side session (session.tenant_id), NOT from the hostname.
 *   On localhost detectTenant() returns "default" which does NOT match.
 *   We therefore use user.tenantId (populated from session.tenant_id) as the
 *   authoritative source, with session.tenantId from the mint as a fallback.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { logger } from '@/shared/lib/logger';
import { fetchGeoSession, type GeoSessionResponse } from '@/shared/api/geoSessionApi';
import {
  setGeoToken,
  setGeoTenantId,
  clearGeoToken,
  GEO_TOKEN_EXPIRED_EVENT,
} from '@/shared/api/geoTokenStore';
import { useAuth } from '@/shared/providers/AuthProvider';

// ─── Module-level dedup cache ─────────────────────────────────────────────────
// Shared across StrictMode double-invoke — only ONE fetch per cycle.

let inflightFetch: Promise<GeoSessionResponse> | null = null;

const getOrFetchGeoSession = (): Promise<GeoSessionResponse> => {
  if (!inflightFetch) {
    inflightFetch = fetchGeoSession().finally(() => {
      inflightFetch = null;
    });
  }
  return inflightFetch;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GeoSessionContextType {
  /** Current geo-access token, or null while loading / on error */
  token: string | null;
  /** True during initial fetch and proactive/forced refresh */
  loading: boolean;
  /** Last error message, or null when healthy */
  error: string | null;
  /**
   * Force-refresh the token immediately.
   * Called e.g. when a maps proxy call returns 401 (token expired early).
   */
  refresh: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const GeoSessionContext = createContext<GeoSessionContextType>({
  token:   null,
  loading: true,
  error:   null,
  refresh: () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

/** Refresh 60 s before the token expires to avoid mid-request 401s */
const REFRESH_BUFFER_MS   = 60_000;       // T - 60 s
/**
 * Minimum gap between two 401-driven re-mints. A single page can have several
 * /api/v1/* calls in flight (autocomplete + reverse geocode + routing), so an
 * expired token produces a burst of 401s that would otherwise trigger one mint
 * each — and a mint that keeps returning an unusable token would spin forever.
 */
const REACTIVE_REFRESH_COOLDOWN_MS = 5_000;
const DEFAULT_LIFETIME_MS = 14 * 60_000;  // 14 min fallback when expiresAt is absent

export const GeoSessionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // user.tenantId is populated from session.tenant_id in the BFF session response —
  // the same value the BFF embeds in the GeoSessionRecord at geo-token mint time.
  // Do NOT use useTenantContext().tenantId here: on localhost detectTenant()
  // returns "default" which never matches the BFF session tenant ("qa-react" etc.).
  const { user } = useAuth();

  const [token,   setToken]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // Incrementing causes the fetch effect to re-run (proactive or forced refresh)
  const [refreshSeq, setRefreshSeq] = useState(0);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Timestamp of the last 401-driven re-mint, for cooldown throttling */
  const lastReactiveRefreshRef = useRef<number>(0);

  const clearTimer = (): void => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const refresh = useCallback((): void => {
    clearTimer();
    inflightFetch = null; // force a new request on next effect run
    setRefreshSeq((s) => s + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getOrFetchGeoSession()
      .then((session) => {
        if (cancelled) return;

        // Write to module-level store so apiClient interceptor can read synchronously.
        // X-Geo-Token and X-Tenant-ID must BOTH be present — the maps proxy validates
        // that the tenant embedded in the GeoSessionRecord matches X-Tenant-ID.
        //
        // Tenant ID resolution priority:
        //   1. session.tenantId — returned by BFF geo-session mint, MOST authoritative:
        //      the BFF embeds this same value in the GeoSessionRecord in Redis.
        //   2. user.tenantId   — from BFF session response (session.tenant_id).
        const resolvedTenantId = session.tenantId ?? user?.tenantId;
        logger.debug('[GeoSession] resolved tenantId for X-Tenant-ID header', {
          sessionTenantId: session.tenantId,
          userTenantId: user?.tenantId,
          resolved: resolvedTenantId,
        });
        setGeoToken(session.token);
        if (resolvedTenantId) {
          setGeoTenantId(resolvedTenantId);
        }
        setToken(session.token);
        setLoading(false);

        // Schedule proactive refresh REFRESH_BUFFER_MS before token expires
        const expiresMs     = session.expiresAt
          ? Date.parse(session.expiresAt)
          : Date.now() + DEFAULT_LIFETIME_MS;
        const msUntilExpiry = expiresMs - Date.now();
        const refreshInMs   = Math.max(0, msUntilExpiry - REFRESH_BUFFER_MS);

        refreshTimerRef.current = setTimeout(() => {
          if (!cancelled) {
            inflightFetch = null;
            setRefreshSeq((s) => s + 1);
          }
        }, refreshInMs);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        logger.warn('[GeoSession] failed to mint token', { error: msg });
        setError(msg);
        setLoading(false);
        // Token stays null — proxy calls will fail gracefully via try/catch at call site
      });

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [refreshSeq, user?.tenantId]);

  // Reactive recovery: re-mint when a maps proxy call reports the token is dead.
  // The proactive timer above covers normal expiry; this covers early revocation
  // (BFF restart, Redis eviction, logout cascade) and timers that fired late
  // because the tab was backgrounded.
  useEffect(() => {
    const onGeoTokenExpired = (): void => {
      const now = Date.now();
      if (now - lastReactiveRefreshRef.current < REACTIVE_REFRESH_COOLDOWN_MS) {
        return; // burst of 401s from concurrent calls — one re-mint is enough
      }
      lastReactiveRefreshRef.current = now;
      logger.debug('[GeoSession] 401 on maps proxy call — re-minting token');
      // Drop the dead token immediately so in-flight requests stop sending it.
      clearGeoToken();
      refresh();
    };

    window.addEventListener(GEO_TOKEN_EXPIRED_EVENT, onGeoTokenExpired);
    return () => {
      window.removeEventListener(GEO_TOKEN_EXPIRED_EVENT, onGeoTokenExpired);
    };
  }, [refresh]);

  // Clear the store token when the provider unmounts (logout / session end)
  useEffect(() => {
    return () => {
      clearGeoToken();
    };
  }, []);

  return (
    <GeoSessionContext.Provider value={{ token, loading, error, refresh }}>
      {children}
    </GeoSessionContext.Provider>
  );
};

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * Returns the shared geo-session context.
 * Use this in components that need to know the token state (e.g. show a
 * "location search unavailable" message while loading is true).
 * The apiClient interceptor reads the token directly from geoTokenStore —
 * no need to call this hook from service files.
 */
export const useGeoSessionContext = (): GeoSessionContextType => {
  return useContext(GeoSessionContext);
};
